import type React from "react";
import { useEffect } from "react";
import { Link } from "react-router";
import { Helmet } from "react-helmet-async";
import { APP_ROUTES } from "@/pages/routes";
import { blogPosts } from "./posts";

const BlogListingPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <div className="flex min-h-screen w-full flex-col bg-white dark:bg-gray-950">
      <Helmet>
        <title>Blog — MycenAI | AI for Psychedelic-Assisted Therapy</title>
        <meta
          name="description"
          content="Articles on psychedelic-assisted therapy, clinical documentation, AI privacy, and PAT practice from MycenAI."
        />
        <link rel="canonical" href="https://mycenai.org/blog/" />
        <meta property="og:title" content="Blog — MycenAI | AI for Psychedelic-Assisted Therapy" />
        <meta property="og:description" content="Articles on psychedelic-assisted therapy, clinical documentation, AI privacy, and PAT practice from MycenAI." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://mycenai.org/blog/" />
      </Helmet>

      {/* Header */}
      <header className="border-gray-200 border-b bg-white/80 backdrop-blur-sm dark:border-gray-800 dark:bg-gray-900/80">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <Link to={APP_ROUTES.SPLASH}>
            <span className="font-bold text-gray-900 text-xl dark:text-white">
              Mycen<span className="text-violet-600 dark:text-violet-400">AI</span>
            </span>
          </Link>
          <div className="flex items-center gap-6">
            <Link
              to="/blog"
              className="hidden font-medium text-gray-700 text-sm transition hover:text-violet-600 sm:inline dark:text-gray-300 dark:hover:text-violet-400"
            >
              Blog
            </Link>
            <Link
              to={APP_ROUTES.RESOURCES}
              className="hidden font-medium text-gray-700 text-sm transition hover:text-violet-600 sm:inline dark:text-gray-300 dark:hover:text-violet-400"
            >
              Resources
            </Link>
            <Link
              to={APP_ROUTES.ABOUT}
              className="hidden font-medium text-gray-700 text-sm transition hover:text-violet-600 sm:inline dark:text-gray-300 dark:hover:text-violet-400"
            >
              About
            </Link>
            <a
              href="https://confideai.ai/blog"
              className="hidden font-medium text-gray-500 text-sm transition hover:text-blue-600 sm:inline dark:text-gray-400 dark:hover:text-blue-400"
            >
              ConfideAI Blog →
            </a>
            <a
              href="https://confideai.ai/welcome/mycenai"
              className="rounded-lg bg-violet-600 px-6 py-2.5 font-semibold text-sm text-white shadow-sm transition hover:bg-violet-700"
            >
              Get Started
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <div className="mx-auto w-full max-w-3xl px-4 py-12 sm:px-6">
          <Link
            to={APP_ROUTES.SPLASH}
            className="mb-8 inline-block text-gray-500 text-sm transition hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
          >
            ← Home
          </Link>

          <h1 className="mb-2 font-bold text-3xl text-gray-900 sm:text-4xl dark:text-white">
            Blog
          </h1>
          <p className="mb-10 text-gray-600 text-lg dark:text-gray-300">
            Articles on psychedelic-assisted therapy, clinical documentation, and AI privacy.
          </p>

          <div className="space-y-8">
            {blogPosts.map((post) => (
              <article
                key={post.slug}
                className="border-gray-200 border-b pb-8 last:border-b-0 dark:border-gray-700"
              >
                <Link to={`/blog/${post.slug}`} className="group block">
                  <h2 className="mb-2 font-semibold text-gray-900 text-xl leading-snug transition group-hover:text-violet-600 dark:text-white dark:group-hover:text-violet-400">
                    {post.title}
                  </h2>
                  <p className="mb-3 text-gray-500 text-sm dark:text-gray-400">
                    {post.date} · {post.readingTime}
                  </p>
                  <p className="mb-3 text-gray-600 leading-relaxed dark:text-gray-300">
                    {post.description}
                  </p>
                  <span className="font-medium text-sm text-violet-600 transition group-hover:text-violet-700 dark:text-violet-400 dark:group-hover:text-violet-300">
                    Read more →
                  </span>
                </Link>
              </article>
            ))}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-gray-200 border-t bg-gray-50 px-6 py-8 dark:border-gray-800 dark:bg-gray-900">
        <div className="mx-auto flex max-w-3xl flex-col items-center gap-4 text-center">
          <p className="text-gray-500 text-sm dark:text-gray-400">
            © 2026 MycenAI. All rights reserved.
          </p>
          <div className="flex gap-6 text-gray-500 text-sm dark:text-gray-400">
            <Link to={APP_ROUTES.FAQ} className="transition hover:text-violet-600 dark:hover:text-violet-400">
              FAQ
            </Link>
            <Link to={APP_ROUTES.PRIVACY} className="transition hover:text-violet-600 dark:hover:text-violet-400">
              Privacy
            </Link>
            <Link to={APP_ROUTES.TERMS} className="transition hover:text-violet-600 dark:hover:text-violet-400">
              Terms
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BlogListingPage;
